import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Spinner,
  Switch,
  Text,
  Textarea,
  useClipboard,
} from "@chakra-ui/react";
import { AddIcon, CopyIcon, WarningIcon } from "@chakra-ui/icons";
import { FaLeaf } from "react-icons/fa";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AwardCriterionDto,
  AwardCriterionType,
  SourcingEventStatus,
  WeightType,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import {
  activeExemptionType,
  AWARD_CRITERION_MODEL_OPTIONS,
  ENVIRONMENT_CRITERION_TYPES,
  ENVIRONMENT_EXEMPTION_TYPES,
  environmentRuleApplies,
  isEnvironmentCriterionType,
  isExemption,
  isExemptionMarker,
  MIN_ENVIRONMENT_WEIGHTING_PERCENT,
  type AwardCriterionModel,
} from "../utils/publicProcurementOptions";
import {
  exemptionTemplateKey,
  exemptionTitleKey,
  isStandardText,
  templateBody,
  type EnvironmentJustificationTemplateKey,
} from "../utils/environmentJustificationTemplates";
import { AwardCriterionCard } from "../components/AwardCriterionCard";
import { WeightSummary } from "../components/WeightSummary";
import {
  DEFAULT_BREAKPOINT_PERCENT,
  PRICE_MODEL_OPTIONS,
  priceModelKey,
  priceModelType,
  usePriceModelDescription,
  type PriceModelKey,
} from "../../../common/priceModel";

// t('Award criteria') t('Award criteria model') t('Lowest price is a price-only award model')
// t('Add criterion') t('Delete criterion') t('Are you sure you want to delete this criterion?')
// t('Delete') t('Criteria are locked after publication')
// t('Climate and environmental weighting (anskaffelsesloven § 5b)')
// t('Exemption from climate and environmental requirements (anskaffelsesloven § 5c)')
// t('Climate and environmental considerations must be weighted at least {{percent}} % as an award criterion. You have weighted them {{weight}} %. To publish with a lower weighting you must document why.')
// t('You have chosen not to weight climate and environmental considerations. Select which exemption applies and document why.')
// t('Exemption reason') t('Justification for the exemption')
// t('Set climate and environment weighting to 30%')
// t('Justification for the lower weighting')
// t('Copy this justification into your konkurransegrunnlag. It is also added to the procurement protocol automatically.')
// t('Copy justification')
// t('No environmental weighting') t('Remove environment criteria')
// t('Any environment criteria you have registered will be removed if you continue.')
// t('This is still the standard text. Adapt it to your procurement — a copied template is not a valid justification.')

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  isActive: boolean;
}

export const AwardCriteriaStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const confirmationModal = useConfirmationModal();

  const isLocked = state.status !== SourcingEventStatus.Draft;
  const awardCriteria = state.eventData.awardCriteria ?? null;
  const selectedModel: AwardCriterionModel | null =
    (["lowestPrice", "bestPriceQualityRatio"] as const).find((k) => awardCriteria?.[k] != null) ?? null;
  const awardSaving = fieldStatus("eventData.awardCriteria");
  const isSavingAwardCriteria = awardSaving === "saving" || awardSaving === "pending";
  const collectSaving = fieldStatus("eventData.collectSupplierPrice");
  const isSavingCollectSupplierPrice = collectSaving === "saving" || collectSaving === "pending";
  const priceModelSaving = fieldStatus("eventData.priceModel");
  const isSavingPriceModel = priceModelSaving === "saving" || priceModelSaving === "pending";
  const selectedPriceModel = priceModelKey(state.eventData.priceModel);
  const breakpointPercent = state.eventData.priceModel?.hybrid?.breakpointPercent ?? DEFAULT_BREAKPOINT_PERCENT;
  const describePriceModel = usePriceModelDescription();

  const bestPriceQualityRatioCriteria: AwardCriterionDto[] = useMemo(
    () => awardCriteria?.bestPriceQualityRatio?.criteria ?? [],
    [awardCriteria]
  );

  // anskaffelsesloven § 5b/§ 5c: climate/environment must be weighted ≥ 30 % on Del II/III, or the
  // buyer must justify the deviation (spec route or a § 5c exemption) before publishing.
  const environmentWeight = useMemo(
    () =>
      bestPriceQualityRatioCriteria
        .filter((c) => ENVIRONMENT_CRITERION_TYPES.includes(c.type))
        .reduce((sum, c) => sum + (c.weight?.number ?? 0), 0),
    [bestPriceQualityRatioCriteria]
  );
  const needsEnvironmentJustification =
    environmentRuleApplies(state.publicContext.foaPart) &&
    environmentWeight < MIN_ENVIRONMENT_WEIGHTING_PERCENT;

  const exemptionChosen = useMemo(
    () => isExemption(bestPriceQualityRatioCriteria),
    [bestPriceQualityRatioCriteria],
  );
  const activeExemption = useMemo(
    () => activeExemptionType(bestPriceQualityRatioCriteria),
    [bestPriceQualityRatioCriteria],
  );
  // Cards exclude the exemption marker — the exemption shows as the justification box, never a
  // criterion card — and so does the weight total the cards feed.
  const visibleCriteria = useMemo(
    () => bestPriceQualityRatioCriteria.filter((c) => !isExemptionMarker(c.type)),
    [bestPriceQualityRatioCriteria],
  );
  // Raw stored value distinguishes "never set" (null/undefined → safe to pre-fill) from "deliberately
  // cleared" ("" → leave empty); environmentJustification is the display/predicate string.
  const rawJustification = awardCriteria?.bestPriceQualityRatio?.environmentWeightingJustification;
  const environmentJustification = rawJustification ?? "";
  const { onCopy: copyJustification } = useClipboard(environmentJustification);

  // Which standard text the box pre-fills with: an active § 5c exemption uses that exemption's
  // template; otherwise the § 5b specification text (requirements in the kravspesifikasjon).
  const activeTemplateKey: EnvironmentJustificationTemplateKey = activeExemption
    ? exemptionTemplateKey(activeExemption)
    : "addressed-in-specification";
  const activeTemplateBody = templateBody(activeTemplateKey, t);
  // Same predicate as the saved flag and the BFF publish gate (any template body, language-aware) so the
  // inline warning and the publish block never disagree — e.g. pasting the other template, or switching
  // language between pasting and saving.
  const showForgotToEditWarning = isStandardText(environmentJustification, t);

  // Every write of the justification also sends whether it is still an unedited standard template
  // (derived here, against the live template) so the BFF publish gate can flag a copied boilerplate.
  const saveEnvironmentJustification = useCallback(
    (value: string | null, debounceMs = 0) =>
      updateField(
        {
          eventData: {
            awardCriteria: {
              bestPriceQualityRatio: {
                environmentWeightingJustification: value,
                environmentWeightingJustificationIsStandardText: value != null && isStandardText(value, t),
              },
            },
          },
        },
        { debounceMs },
      ),
    [updateField, t]
  );

  // A § 5c exemption is mutually exclusive with any weighted environment criterion, so it removes all
  // environment rows and stores a single exemption marker (the BFF fills its standard description),
  // pre-filling that exemption's template. Confirm first if any environment weighting would be discarded.
  const applyExemption = useCallback(
    (exemptionType: AwardCriterionType) => {
      const updated = bestPriceQualityRatioCriteria
        .filter((c) => !isEnvironmentCriterionType(c.type))
        .concat({
          type: exemptionType,
          name: "",
          description: "",
          weight: undefined,
        });
      return updateField({
        eventData: {
          awardCriteria: {
            bestPriceQualityRatio: {
              criteria: updated,
              environmentWeightingJustification: templateBody(exemptionTemplateKey(exemptionType), t),
              environmentWeightingJustificationIsStandardText: true,
            },
          },
        },
      });
    },
    [bestPriceQualityRatioCriteria, updateField, t],
  );

  // Switch which § 5c exemption applies (from the reason selector). The justification always follows the
  // chosen ground: it is reset to that ground's standard template so the protocol and the emitted notice
  // never name different § 5c grounds. If the buyer had adapted the justification, confirm first, since
  // switching discards that authored text.
  const switchExemption = useCallback(
    (exemptionType: AwardCriterionType) => {
      const applySwitch = () => {
        // Clear the marker's Doffin description so the BFF re-derives the standard text for the NEW ground.
        const updated = bestPriceQualityRatioCriteria.map((c) =>
          isExemptionMarker(c.type) ? { ...c, type: exemptionType, description: "" } : c,
        );
        void updateField({
          eventData: {
            awardCriteria: {
              bestPriceQualityRatio: {
                criteria: updated,
                environmentWeightingJustification: templateBody(exemptionTemplateKey(exemptionType), t),
                environmentWeightingJustificationIsStandardText: true,
              },
            },
          },
        });
      };
      const hasAdaptedJustification =
        environmentJustification !== "" && !isStandardText(environmentJustification, t);
      if (!hasAdaptedJustification) {
        applySwitch();
        return;
      }
      confirmationModal.show({
        title: t(exemptionTitleKey(exemptionType)),
        description: t("Your own justification will be replaced with the standard text for this exemption."),
        confirmButtonText: t("Replace justification") ?? "",
        onConfirmed: async () => {
          applySwitch();
        },
      });
    },
    [bestPriceQualityRatioCriteria, updateField, confirmationModal, environmentJustification, t],
  );

  // Bring the climate/environment weighting up to the 30 % floor straight from the warning box. Drops
  // any § 5c exemption marker, then: if an environment criterion already exists (the sub-30 % case),
  // bump the first one so the TOTAL weighting reaches 30 %; otherwise (coming from an exemption) add a
  // fresh criterion at 30 %. The BFF then classifies every environment row as a full award criterion
  // (vei A). Weight belongs on the criterion — the exemption codes never carry a weight (DFØ rule).
  const weightEnvironmentAtFloor = useCallback(() => {
    const nonMarker = bestPriceQualityRatioCriteria.filter((c) => !isExemptionMarker(c.type));
    const environmentRows = nonMarker.filter((c) => isEnvironmentCriterionType(c.type));
    let updated: AwardCriterionDto[];
    if (environmentRows.length === 0) {
      updated = nonMarker.concat({
        type: AwardCriterionType.QualityEnvironmentalCriteria,
        name: "",
        description: "",
        weight: { number: MIN_ENVIRONMENT_WEIGHTING_PERCENT, type: WeightType.Percentage },
      });
    } else {
      const othersWeight = environmentRows
        .slice(1)
        .reduce((sum, c) => sum + (c.weight?.number ?? 0), 0);
      const firstWeight = Math.max(0, MIN_ENVIRONMENT_WEIGHTING_PERCENT - othersWeight);
      let bumped = false;
      updated = nonMarker.map((c) => {
        if (isEnvironmentCriterionType(c.type) && !bumped) {
          bumped = true;
          return { ...c, weight: { number: firstWeight, type: WeightType.Percentage } };
        }
        return c;
      });
    }
    return updateField({
      eventData: { awardCriteria: { bestPriceQualityRatio: { criteria: updated } } },
    });
  }, [bestPriceQualityRatioCriteria, updateField]);

  const chooseExemption = useCallback(
    (exemptionType: AwardCriterionType) => {
      // The exemption removes every environment row and overwrites the justification with the standard
      // template — so confirm first whenever that would discard work: any environment criterion (weighted
      // or not, but not the marker itself) or a buyer-adapted justification. The buyer picks which § 5c
      // ground applies from the menu; the reason selector lets them change it afterwards.
      const hasEnvironmentCriterion = bestPriceQualityRatioCriteria.some(
        (c) => isEnvironmentCriterionType(c.type) && !isExemptionMarker(c.type),
      );
      const hasAdaptedJustification =
        environmentJustification !== "" && !isStandardText(environmentJustification, t);
      if (!hasEnvironmentCriterion && !hasAdaptedJustification) {
        void applyExemption(exemptionType);
        return;
      }
      confirmationModal.show({
        title: t(exemptionTitleKey(exemptionType)),
        description:
          hasEnvironmentCriterion && hasAdaptedJustification
            ? t("Your environment criteria will be removed and your own justification replaced if you continue.")
            : hasEnvironmentCriterion
              ? t("Any environment criteria you have registered will be removed if you continue.")
              : t("Your own justification will be replaced with the standard text for this exemption."),
        confirmButtonText: hasEnvironmentCriterion
          ? (t("Remove environment criteria") ?? "")
          : (t("Replace justification") ?? ""),
        onConfirmed: async () => {
          await applyExemption(exemptionType);
        },
      });
    },
    [bestPriceQualityRatioCriteria, applyExemption, confirmationModal, environmentJustification, t],
  );

  // Standard-template management. When no justification is required (>= 30 % / rule N/A) drop only the
  // unedited standard text — a buyer-adapted justification is kept (hidden) so a transient bump to
  // >= 30 % and back does not destroy it. When required, pre-fill the standard text only if none was ever
  // set (rawJustification == null); a deliberately-cleared box ("") is left empty (the REQUIRED gate
  // reminds them) and survives a reload — it is never fought by a re-prefill.
  useEffect(() => {
    if (selectedModel !== "bestPriceQualityRatio" || isLocked) return;
    if (!needsEnvironmentJustification) {
      if (rawJustification && isStandardText(rawJustification, t)) saveEnvironmentJustification(null);
      return;
    }
    if (rawJustification == null) saveEnvironmentJustification(activeTemplateBody);
  }, [
    selectedModel,
    needsEnvironmentJustification,
    isLocked,
    rawJustification,
    activeTemplateBody,
    saveEnvironmentJustification,
    t,
  ]);

  const handleModelChange = useCallback(
    (model: AwardCriterionModel) => {
      // Both sides must appear in the patch: merge-patch keeps omitted keys, so the
      // BFF would see both lowestPrice and bestPriceQualityRatio set otherwise and
      // reject with IllegalArgumentException in AwardCriteriaDto.toDomain.
      if (model === "lowestPrice") {
        updateField({
          eventData: { awardCriteria: { lowestPrice: { selected: true }, bestPriceQualityRatio: null } },
        });
      } else {
        // BFF seeds defaults via withDefaults() when bestPriceQualityRatio is set with no fields.
        updateField({
          eventData: {
            awardCriteria: { lowestPrice: null, bestPriceQualityRatio: {} },
          },
        });
      }
    },
    [updateField]
  );

  const savePriceModel = useCallback(
    (model: PriceModelKey, percent: number, debounceMs = 0) =>
      // All variants must appear in the patch: merge-patch keeps omitted keys, so the
      // BFF would otherwise see several variants set and reject in PriceModelDto.toDomain.
      updateField(
        {
          eventData: {
            priceModel: {
              proportional: model === "proportional" ? { selected: true } : null,
              linear: model === "linear" ? { selected: true } : null,
              hybrid: model === "hybrid" ? { breakpointPercent: percent } : null,
            },
          },
        },
        { debounceMs },
      ),
    [updateField]
  );

  const saveCriteria = useCallback(
    (criteria: AwardCriterionDto[], debounceMs = 0) =>
      updateField(
        { eventData: { awardCriteria: { bestPriceQualityRatio: { criteria } } } },
        { debounceMs },
      ),
    [updateField]
  );

  const handleCriterionChange = useCallback(
    (index: number, next: AwardCriterionDto) => {
      const prev = bestPriceQualityRatioCriteria[index];
      const updated = bestPriceQualityRatioCriteria.map((c, i) => (i === index ? next : c));
      // Crossing into a different § 5b/§ 5c path invalidates a standard justification written for the
      // previous one, so clear it in the same patch. We react only to a committed path change —
      // reclassifying away from the environment type, or a concrete new weight landing in another
      // range — never to the transient empty-weight state mid-typing (which would wipe text the user
      // is merely editing around; clearing to 0 % deliberately is the "Uvesentlig" template's job).
      const pathChanged =
        isEnvironmentCriterionType(prev.type) &&
        prev.type !== next.type &&
        (!isEnvironmentCriterionType(next.type) || next.weight?.number != null);
      // Only discard the boilerplate standard text on a path change — never a justification the buyer
      // has adapted, and never one another (still-weighted) environment criterion still needs.
      if (pathChanged && environmentJustification && isStandardText(environmentJustification, t)) {
        return updateField(
          {
            eventData: {
              awardCriteria: {
                bestPriceQualityRatio: {
                  criteria: updated,
                  environmentWeightingJustification: null,
                  environmentWeightingJustificationIsStandardText: false,
                },
              },
            },
          },
          { debounceMs: 300 },
        );
      }
      return saveCriteria(updated, 300);
    },
    [bestPriceQualityRatioCriteria, saveCriteria, environmentJustification, updateField, t]
  );

  const handleAdd = useCallback(() => {
    // A neutral Quality row; the buyer picks "Miljø" (and any number of environment criteria) via
    // the in-card type picker, which seeds the § 5b floor weight.
    const next: AwardCriterionDto = {
      type: AwardCriterionType.Quality,
      name: "",
      description: "",
      weight: undefined,
    };
    return saveCriteria([...bestPriceQualityRatioCriteria, next]);
  }, [bestPriceQualityRatioCriteria, saveCriteria]);

  const handleDelete = useCallback(
    (index: number) => {
      confirmationModal.show({
        title: t("Delete criterion"),
        description: t("Are you sure you want to delete this criterion?"),
        confirmButtonText: t("Delete") ?? "",
        confirmButtonColor: "red",
        onConfirmed: async () => {
          // Never clear the § 5b/§ 5c justification on delete: at 0 % environment weighting it is still
          // required on Del II/III, and a buyer-adapted text must not be lost. Just save the criteria.
          await saveCriteria(bestPriceQualityRatioCriteria.filter((_, i) => i !== index));
        },
      });
    },
    [bestPriceQualityRatioCriteria, saveCriteria, confirmationModal, t]
  );

  const selectedModelDescription = selectedModel
    ? AWARD_CRITERION_MODEL_OPTIONS.find((o) => o.value === selectedModel)?.descriptionKey
    : null;

  // The three § 5c exemptions, labelled by their template title, for the reason selector.
  const exemptionOptions = ENVIRONMENT_EXEMPTION_TYPES.map((type) => ({
    type,
    labelKey: exemptionTitleKey(type),
  }));

  const environmentJustificationBlock = (
    <Alert status="warning" borderRadius="lg" my="3" alignItems="flex-start" flexDirection="column">
      <Flex>
        <AlertIcon />
        <AlertTitle>
          {exemptionChosen
            ? t("Exemption from climate and environmental requirements (anskaffelsesloven § 5c)")
            : t("Climate and environmental weighting (anskaffelsesloven § 5b)")}
        </AlertTitle>
      </Flex>
      <AlertDescription mt="2">
        {exemptionChosen
          ? t(
              "You have chosen not to weight climate and environmental considerations. Select which exemption applies and document why.",
            )
          : t(
              "Climate and environmental considerations must be weighted at least {{percent}} % as an award criterion. You have weighted them {{weight}} %. To publish with a lower weighting you must document why.",
              { percent: MIN_ENVIRONMENT_WEIGHTING_PERCENT, weight: environmentWeight },
            )}
      </AlertDescription>

      {exemptionChosen && (
        <FormControl mt="4" isDisabled={isLocked}>
          <FormLabel mb="2">{t("Exemption reason")}</FormLabel>
          <Select
            rounded="md"
            bg="smBackgroundPrimary"
            value={activeExemption ?? ""}
            isDisabled={isLocked}
            onChange={(e) => switchExemption(e.target.value as AwardCriterionType)}
          >
            {exemptionOptions.map((o) => (
              <option key={o.type} value={o.type}>{t(o.labelKey)}</option>
            ))}
          </Select>
        </FormControl>
      )}
      <Button
        mt="3"
        size="sm"
        variant="outline"
        leftIcon={<FaLeaf />}
        isDisabled={isLocked}
        onClick={() => weightEnvironmentAtFloor()}
      >
        {t("Set climate and environment weighting to 30%")}
      </Button>

      <FormControl mt="4" isDisabled={isLocked}>
        <FormLabel mb="2">
          {exemptionChosen ? t("Justification for the exemption") : t("Justification for the lower weighting")}
        </FormLabel>
        <Textarea
          rounded="md"
          bg="smBackgroundPrimary"
          rows={5}
          value={environmentJustification}
          isDisabled={isLocked}
          onChange={(e) => saveEnvironmentJustification(e.target.value, 300)}
        />
        {showForgotToEditWarning && (
          <Flex align="center" gap="2" mt="2" color="smWarning">
            <WarningIcon boxSize="3.5" />
            <Text fontSize="sm">
              {t(
                "This is still the standard text. Adapt it to your procurement — a copied template is not a valid justification.",
              )}
            </Text>
          </Flex>
        )}
        <Flex align="center" justify="space-between" gap="2" mt="2">
          <FormHelperText mt="0">
            {t(
              "Copy this justification into your konkurransegrunnlag. It is also added to the procurement protocol automatically.",
            )}
          </FormHelperText>
          <Button
            size="xs"
            variant="ghost"
            leftIcon={<CopyIcon />}
            onClick={copyJustification}
            isDisabled={!environmentJustification}
          >
            {t("Copy justification")}
          </Button>
        </Flex>
      </FormControl>
    </Alert>
  );

  return (
    <Box pb="8" pt="10">
      <Flex justify="space-between" align="center" mb="6">
        <Heading as="h2" size="md">
          {t("Award criteria")} {isSavingAwardCriteria && <Spinner size="xs" />}
        </Heading>
      </Flex>

      {isLocked && (
        <Alert status="info" borderRadius="lg" mb="6">
          <AlertIcon />
          <AlertDescription>{t("Criteria are locked after publication")}</AlertDescription>
        </Alert>
      )}

      <FormControl mb="6" isDisabled={isLocked}>
        <Flex align="center" justify="space-between" gap="4">
          <Box>
            <Flex align="center" gap="2">
              <FormLabel mb="0">{t("Ask suppliers to enter a price")}</FormLabel>
              {isSavingCollectSupplierPrice && <Spinner size="xs" />}
            </Flex>
            <FormHelperText mt="1">
              {t(
                "When off, suppliers are not asked for a total price. You enter or override prices yourself during evaluation.",
              )}
            </FormHelperText>
          </Box>
          <Switch
            isChecked={state.eventData.collectSupplierPrice}
            onChange={(e) => updateField({ eventData: { collectSupplierPrice: e.target.checked } })}
          />
        </Flex>
      </FormControl>

      <FormControl mb="6" isDisabled={isLocked}>
        <FormLabel>{t("Award criteria model")}</FormLabel>
        <Select
          rounded="md"
          value={selectedModel ?? ""}
          onChange={(e) => handleModelChange(e.target.value as AwardCriterionModel)}
        >
          {AWARD_CRITERION_MODEL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
          ))}
        </Select>
        {selectedModelDescription && (
          <FormHelperText>{t(selectedModelDescription)}</FormHelperText>
        )}
      </FormControl>

      {selectedModel === "lowestPrice" && (
        <Alert status="info" borderRadius="lg" mb="6">
          <AlertIcon />
          <AlertDescription>{t("Lowest price is a price-only award model")}</AlertDescription>
        </Alert>
      )}

      {selectedModel === "bestPriceQualityRatio" && (
        <>
          <FormControl mb="6" isDisabled={isLocked}>
            <Flex align="center" gap="2">
              <FormLabel mb="0">{t("Price model")}</FormLabel>
              {isSavingPriceModel && <Spinner size="xs" />}
            </Flex>
            <Select
              rounded="md"
              mt="2"
              value={selectedPriceModel}
              onChange={(e) => savePriceModel(e.target.value as PriceModelKey, breakpointPercent)}
            >
              {PRICE_MODEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{t(opt.type)}</option>
              ))}
            </Select>
            <FormHelperText>
              {describePriceModel(priceModelType(state.eventData.priceModel), breakpointPercent)}
            </FormHelperText>
          </FormControl>

          {selectedPriceModel === "hybrid" && (
            <FormControl mb="6" isDisabled={isLocked} maxW="64">
              <FormLabel>{t("Breakpoint (% above the lowest price)")}</FormLabel>
              <NumberInput
                min={1}
                max={99}
                step={1}
                precision={0}
                value={breakpointPercent}
                onChange={(_, value) => {
                  // Chakra clamps min/max only on blur, so skip out-of-range keystrokes
                  // (e.g. a half-typed "1" before "5") rather than PATCHing an invalid value.
                  const rounded = Math.round(value);
                  if (rounded >= 1 && rounded <= 99) savePriceModel("hybrid", rounded, 300);
                }}
              >
                <NumberInputField rounded="md" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          )}

          <Box>
            {bestPriceQualityRatioCriteria.map((c, i) =>
              isExemptionMarker(c.type) ? null : (
                <AwardCriterionCard
                  key={i}
                  eventId={eventId}
                  index={i}
                  criterion={c}
                  isLocked={isLocked}
                  onChange={(next) => handleCriterionChange(i, next)}
                  onDelete={handleDelete}
                  onChooseExemption={chooseExemption}
                />
              ),
            )}
          </Box>
          <Button
            leftIcon={<AddIcon />}
            onClick={handleAdd}
            isDisabled={isLocked}
            variant="outline"
            size="sm"
            mt="2"
          >
            {t("Add criterion")}
          </Button>
          <WeightSummary criteria={visibleCriteria} />

          {needsEnvironmentJustification && environmentJustificationBlock}
        </>
      )}

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />

      {confirmationModal.modal}
    </Box>
  );
};
